import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, CreditCard } from "lucide-react";

export default function BillingReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const cancelled = params.get("cancelled") === "1";
  const kind = params.get("type") || "payment";
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (cancelled) return;
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("error"); return; }
      } catch (err) { console.warn("Billing status check failed, retrying", err?.message); }
      if (tries++ < 8) setTimeout(poll, 1600);
      else setStatus("pending");
    };
    poll();
  }, [location.search, cancelled, params]);

  const Card = ({ icon: Icon, color, title, msg }) => (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center animate-fade-up" data-testid="billing-return">
        <div className={`mx-auto h-16 w-16 rounded-2xl flex items-center justify-center mb-5 ${color}`}><Icon className="h-8 w-8" /></div>
        <h1 className="font-display font-extrabold text-2xl text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-2">{msg}</p>
        <Button onClick={() => navigate("/billing")} className="mt-6 gap-2" data-testid="billing-return-btn"><CreditCard className="h-4 w-4" /> Back to Billing</Button>
      </div>
    </div>
  );

  if (status === "checking")
    return <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500"><Loader2 className="h-7 w-7 animate-spin text-primary" /> Confirming your payment…</div>;
  if (status === "paid")
    return kind === "subscription"
      ? <Card icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" title="Auto-pay is on!" msg={`${amount ? fmtMoney(amount) + " " : ""}will be charged to your card automatically each month. This month is covered.`} />
      : <Card icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" title="Fee paid — thank you!" msg={`${amount ? fmtMoney(amount) + " " : ""}has been received. This month's management fee is settled.`} />;
  if (status === "pending")
    return <Card icon={Loader2} color="bg-amber-100 text-amber-600" title="Payment processing" msg="Your payment is being confirmed and will show in Billing shortly." />;
  if (status === "cancelled")
    return <Card icon={XCircle} color="bg-slate-100 text-slate-500" title="Checkout cancelled" msg="No charge was made. You can pay whenever you're ready." />;
  return <Card icon={XCircle} color="bg-rose-100 text-rose-600" title="Something went wrong" msg="We couldn't confirm this payment. Please try again." />;
}
